import { Component, OnInit, NgZone, ViewChild, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ModalController, NavParams, IonContent, IonicModule } from '@ionic/angular';
import {
  PositionedDeviceData,
  DeviceCardModalResult,
  Zone,
  Level,
  Group,
  PositionUpdateFields,
  EditAdditionalFields
} from 'src/app/model';
import { HelperProvider } from 'src/app/providers/helper/helper';
import { ApiService } from 'src/app/providers/api/api.service';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { CalibrateService } from 'src/app/providers/calibrate/calibrate.service';
import { Subscription } from 'rxjs';
import { DeviceBindComponent } from 'src/app/components/device-bind/device-bind.component';
import { LabelsSelectComponent } from 'src/app/components/labels-select/labels-select.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-device-edit',
  templateUrl: './device-edit.modal.html',
  styleUrls: ['./device-edit.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [IonicModule, FormsModule, CommonModule, DeviceBindComponent, LabelsSelectComponent],
})
export class DeviceEditComponent implements OnInit, OnDestroy {
  private labelsSub: Subscription;
  public device?: PositionedDeviceData;
  public group?: Group;
  public level?: Level;
  public zone?: Zone;
  public customIdInput = '';
  public calibrated: boolean | undefined = undefined;

  @ViewChild(IonContent, { static: false }) content: IonContent;

  constructor(
    private modalController: ModalController,
    private navParams: NavParams,
    private helper: HelperProvider,
    private api: ApiService,
    private dmStore: DmStoreService,
    private ngZone: NgZone,
    private calibrateService: CalibrateService,
  ) { }

  async ngOnInit() {
    await this.dmStore.fetchZones({});
    this.device = await this.navParams.get(EditAdditionalFields.Device);
    this.zone = await this.navParams.get(EditAdditionalFields.Zone);
    this.level = await this.navParams.get(EditAdditionalFields.Level);
    this.group = await this.navParams.get(EditAdditionalFields.Group);
    this.customIdInput = this.device.custom_id || '';
    this.calibrated = this.device?.calibration_status;

    this.dmStore.selectZone(this.zone?.id);
    this.labelsSub = this.dmStore.labels$.subscribe(labels => {
      if (!labels.loading && labels.data.length && this.device) {
        this.dmStore.selectLabelsForEdit(this.device.labels);
      }
    });
  }

  ngOnDestroy() {
    this.labelsSub?.unsubscribe();
  }

  public back = () => this.modalController.dismiss();

  public async unbind() {
    if (!this.device?.position_id) {
      return;
    }

    this.helper.showLoading();
    const unbind = await this.api.devices.undind(this.device.device_id, this.device.position_id);

    if (unbind.error) {
      this.helper.alert(unbind.error);
      this.helper.hideLoading();
      return;
    }
    this.dmStore.unbindDevice(this.device.device_id, this.device.position_id);

    const result: DeviceCardModalResult = {
      unbind: true,
    };


    this.helper.hideLoading();
    this.modalController.dismiss(result);
  }

  public async save() {
    if (!this.device?.position_id) {
      return;
    }

    const updatedLabels = await this.dmStore.updateLabels(this.device.position_id, this.device.labels);

    if (this.customIdInput === this.device.custom_id) {
      // only labels has been updated
      if (updatedLabels) {
        this.helper.showToast('Device has been updated');
        this.dmStore.updateDevice(this.device.device_id);
        this.modalController.dismiss();
      }
      return;
    }

    this.helper.showLoading();

    if (this.customIdInput !== this.device.custom_id) {
      const position: PositionUpdateFields = {
        custom_id: this.customIdInput || null,
        lat: this.device.lat || 0,
        lon: this.device.lon || 0,
        group_id: this.device.group_id || 0,
        group_inner_id: this.device.group_inner_id || 0,
      };

      const result = await this.api.positions.update(position, this.device.position_id, this.helper);

      if (!result) {
        this.helper.hideLoading();
        return;
      }
    }

    this.helper.showToast('Device has been updated');
    this.dmStore.updateDevice(this.device.device_id);
    this.helper.hideLoading();
    this.modalController.dismiss();
  }

  public calibratorStatus(): string {
    if (this.calibrated === true) {
      return 'success';
    } else if (this.calibrated === false) {
      return 'danger';
    } else {
      return 'primary';
    }
  }

  public calibrate() {
    if (!this.device) {
      return;
    }

    this.calibrateService.start(
      this.device.device_id,
      res => this.ngZone.run(() => {
        this.calibrated = !!res;
      }),
      () => this.helper.alert('Calibrate service isn\'t working on your device'),
    );
  }

  public blurInput() {
    this.content.scrollToBottom(500);
  }
}
